<template>
  <div v-if="classCostData != null">
    <h2 v-if="mode == 'activation'">Account Activation</h2>
    <h2 v-else-if="mode == 'addclasses'">Add Classes</h2>
    <h2 v-else-if="mode == 'extend'">Subscription Renewal</h2>
    <div class="tw-mb-4">
      <SchoolActivationPeriod
        v-if="classCostData"
        :spelling-period="classCostData.subscriptions.spelling.period"
        :maths-period="classCostData.subscriptions.maths.period"
      />
    </div>
    <form @submit.prevent="submit">
      <div
        v-if="showCountryField"
        class="sf-form-group tw-pb-4 md:tw-grid md:tw-grid-cols-2 md:tw-gap-4"
      >
        <label> Select the country where your school is located </label>
        <select v-model="countryCode" class="sf-form-control tw-w-full">
          <option
            v-for="c in countryOptions"
            :key="c.id"
            :value="c.countryCode"
          >
            {{ c.countryName }}
          </option>
        </select>
      </div>
      <div
        class="sf-form-group tw-pb-4 md:tw-grid md:tw-grid-cols-2 md:tw-gap-4"
      >
        <div>
          <label class="tw-block"> Your reference / PO Number </label>
          (Optional)
        </div>
        <div>
          <input
            type="text"
            v-model="schoolReference"
            class="sf-form-control tw-w-full"
          />
        </div>
      </div>
      <SchoolActivationClassesField
        v-if="classCostData?.availableTrial?.type != 'spelling'"
        subject="spelling"
        :mode="mode"
        :class-cost="spellingClassCost"
        :current-subscription-class-count="currentSpellingClassCount"
        v-model="spellingClassCount"
      />
      <SchoolActivationClassesField
        v-if="mathsAvailable && classCostData?.availableTrial?.type != 'maths'"
        subject="maths"
        :mode="mode"
        :class-cost="mathsClassCost"
        :current-subscription-class-count="currentMathsClassCount"
        v-model="mathsClassCount"
      />
      <div
        v-if="classCostData?.availableTrial"
        class="sf-form-group tw-pb-4 md:tw-grid md:tw-grid-cols-2 md:tw-gap-4"
      >
        <div></div>
        <label>
          <input type="checkbox" v-model="activateTrial" />
          Activate {{ classCostData.availableTrial.type }}
          {{ classCostData.availableTrial.length }} days trial
        </label>
      </div>
      <div class="tw-flex tw-justify-end">
        <table class="sf-table tw-text-right">
          <tbody>
            <tr>
              <td>Spelling classes</td>
              <td class="tw-text-right">
                {{ spellingClassCount }} x
                {{ $filters.renderMoney(spellingClassCost) }}
              </td>
              <td class="tw-text-right">
                {{
                  $filters.renderMoney(spellingClassCost * spellingClassCount)
                }}
              </td>
            </tr>
            <tr v-if="mathsAvailable">
              <td>Maths classes</td>
              <td class="tw-text-right">
                {{ mathsClassCount }} x
                {{ $filters.renderMoney(mathsClassCost) }}
              </td>
              <td class="tw-text-right">
                {{ $filters.renderMoney(mathsClassCost * mathsClassCount) }}
              </td>
            </tr>
            <tr v-if="mathsAvailable">
              <td>Spelling + Maths class discount</td>
              <td class="tw-text-right">
                {{ comboCount }} x {{ $filters.renderMoney(comboDiscount) }}
              </td>
              <td class="tw-text-right">
                -{{ $filters.renderMoney(totalDiscount) }}
              </td>
            </tr>
            <tr class="tw-font-bold">
              <td>Total cost before VAT</td>
              <td></td>
              <td class="tw-text-right">
                {{ $filters.renderMoney(totalCost) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div>
        <div class="tw-text-danger tw-font-bold tw-mt-2">
          <span v-if="showMinClassesError"> At least 1 class is required </span>
          &nbsp;
        </div>
        <button
          type="submit"
          :disabled="showMinClassesError"
          class="sf-btn-primary"
        >
          <template v-if="mode == 'activation'"> Activate </template>
          <template v-else-if="mode == 'addclasses'"> Add Classes </template>
          <template v-else-if="mode == 'extend'"> Renew </template>
        </button>
      </div>
    </form>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import {
  SfCountry,
  SfPurchaseType,
  SfStoreSubscriptionsCost,
} from "@/types/generalTypes";
import apiCall from "@/func/net/apiCall";
import { isMathsAvailable, refreshUser } from "@/state/appState";
import SchoolActivationClassesField from "./SchoolActivationClassesField.vue";
import SchoolActivationPeriod from "@/views/site/SchoolActivationPeriod.vue";
import { getSchoolDaysToExpire } from "@/func/schoolFunctions";

export default defineComponent({
  components: { SchoolActivationClassesField, SchoolActivationPeriod },
  emits: ["success"],
  props: {
    mode: { type: String as PropType<SfPurchaseType>, required: true },
    defaultData: {
      type: Object as PropType<{
        mathsClassCount: number;
        spellingClassCount: number;
        schoolReference: string;
        activateTrial: boolean;
      }>,
      required: true,
    },
  },
  data() {
    return {
      countries: null as SfCountry[] | null,
      countryCode: "GB",
      schoolReference: "",
      classCostData: null as SfStoreSubscriptionsCost | null,
      spellingClassCount: 0,
      mathsClassCount: 0,
      activateTrial: false,
    };
  },
  computed: {
    mathsAvailable() {
      return isMathsAvailable('school', 'store');
    },
    spellingClassCost() {
      return this.classCostData?.subscriptions.spelling.cost || 0;
    },
    mathsClassCost() {
      return this.classCostData?.subscriptions.maths.cost  || 0;
    },
    comboDiscount() {
      return this.classCostData?.comboDiscount.discount || 0;
    },
    school() {
      return this.$currentUser.value?.school || null;
    },
    subscriptions() {
      return this.school?.subscriptions || null;
    },
    futureSubscriptions() {
      return this.school?.futureSubscriptions || null;
    },
    currentSpellingClassCount() {
      return this.subscriptions?.spelling &&
        !this.subscriptions.spelling.is_trial
        ? this.subscriptions.spelling.classCount
        : 0;
    },
    currentMathsClassCount() {
      return this.subscriptions?.maths && !this.subscriptions.maths.is_trial
        ? this.subscriptions.maths.classCount
        : 0;
    },
    showMinClassesError() {
      return this.spellingClassCount < 1 && this.mathsClassCount < 1;
    },
    showCountryField(): boolean {
      return (
        this.school?.country_code === undefined ||
        this.school.country_code === null
      );
    },
    countryOptions(): SfCountry[] {
      if (!this.countries) {
        this.refreshCountries();
        return [];
      }
      return this.countries;
    },
    comboCount() {
      const spelingCredit = Math.max(
        this.currentSpellingClassCount - this.currentMathsClassCount,
        0,
      );
      const mathsCredit = Math.max(
        this.currentMathsClassCount - this.currentSpellingClassCount,
        0,
      );
      return Math.min(
        this.spellingClassCount + spelingCredit,
        this.mathsClassCount + mathsCredit,
      );
    },
    totalDiscount() {
      return this.comboDiscount * this.comboCount;
    },
    totalCost() {
      return (
        this.spellingClassCount * this.spellingClassCost +
        this.mathsClassCount * this.mathsClassCost -
        this.totalDiscount
      );
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      if (this.evaluateRedirect()) {
        return;
      } else {
        this.refreshClassCost();
        this.spellingClassCount = this.defaultData.spellingClassCount;
        this.mathsClassCount = this.defaultData.mathsClassCount;
        this.schoolReference = this.defaultData.schoolReference;
        this.activateTrial = this.defaultData.activateTrial;
        if (this.mode == "extend") {
          this.spellingClassCount =
            this.spellingClassCount || this.currentSpellingClassCount;
          this.mathsClassCount =
            this.mathsClassCount || this.currentMathsClassCount;
        }
      }
    },
    evaluateRedirect() {
      const daysToExpire = getSchoolDaysToExpire(
        this.subscriptions,
        this.futureSubscriptions,
      );
      if (daysToExpire < 1) {
        if (this.mode != "activation") {
          this.$router.replace("/register/school/activation");
          return true;
        }
      } else if (daysToExpire <= 90) {
        if (this.mode != "extend") {
          this.$router.replace("/register/school/extend");
          return true;
        }
      } else if (this.mode != "addclasses") {
        this.$router.replace("/register/school/add-classes");
        return true;
      }
      return false;
    },
    async refreshClassCost() {
      const params = {
        kind: this.mode,
      };
      const resData = await apiCall("account/class-cost", params);
      if (resData.success) {
        this.classCostData = resData.data as SfStoreSubscriptionsCost;
        if (this.classCostData.availableTrial) {
          this.activateTrial = this.classCostData.availableTrial.type != null;
        }
      }
    },
    async submit() {
      if (this.spellingClassCost < 1 && this.mathsClassCost < 1) {
        return;
      }
      if (this.showCountryField) {
        const postData = { country_code: this.countryCode };
        const resData = await apiCall(
          "school/" + this.school?.id,
          null,
          { method: "PATCH" },
          postData,
        );
        if (!resData.success) {
          return;
        }
        refreshUser();
      }
      this.$emit("success", {
        spellingClassCount: this.spellingClassCount,
        mathsClassCount: this.mathsClassCount,
        schoolReference: this.schoolReference,
        activateTrial: this.activateTrial,
      });
    },
    async refreshCountries() {
      const resData = await apiCall("country");
      if (resData.success) {
        this.countries = resData.data.items;
      }
    },
  },
  watch: {
    mode() {
      this.init();
    },
  },
});
</script>
